import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell } from '@phosphor-icons/react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu';
import { formatAlertCopy } from '../alerts/budgetAlertCardUtils';
import { useEmergencyFundAlertsQuery } from '../../features/emergencyFund/queries';
import { useBudgetAlertsQuery } from '../../features/budgets/queries';
import { useSnapshotsTimelineQuery } from '../../features/snapshots/queries';
import { useSelfieCycle } from '../../store/SelfieCycleContext';
import { useCycleFinancialOverview } from '../../hooks/useCycleFinancialOverview';
import { buildDashboardAlerts } from '../../utils/dashboardAlerts';
import { paths } from '../../routes/paths';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';

/**
 * Sino da topbar — alertas de orçamento/FE (sem inbox de notificações).
 */
export function NotificationsBell({ className }) {
  const navigate = useNavigate();
  const { currentCycle: activeCycle } = useSelfieCycle();
  const { overview, monthlySummary } = useCycleFinancialOverview(activeCycle);

  const { data: timeline = [] } = useSnapshotsTimelineQuery();
  const { data: efApiAlerts = [] } = useEmergencyFundAlertsQuery();
  const { data: budgetAlerts = [] } = useBudgetAlertsQuery({ is_dismissed: false });

  const alerts = useMemo(
    () =>
      buildDashboardAlerts(overview, {
        hasIncome: Number(monthlySummary?.income?.total || 0) > 0,
        hasSnapshots: timeline.length > 0,
        efApiAlerts,
        budgetAlerts,
        maxItems: 8,
      }),
    [overview, monthlySummary, timeline, efApiAlerts, budgetAlerts],
  );

  const badgeLabel = alerts.length > 9 ? '9+' : String(alerts.length);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* ds/nav/notifications-bell — 40×40, $radius-ds-md, ponto 6px $danger-fg */}
        <button
          type="button"
          className={cn(
            'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-ds-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
            className,
          )}
          aria-label={
            alerts.length > 0
              ? `${pt.nav.notificationsBell} (${badgeLabel})`
              : pt.nav.notificationsBell
          }
        >
          <Bell className="h-5 w-5" aria-hidden />
          {alerts.length > 0 ? (
            <span
              className="absolute right-2 top-2 h-1.5 w-1.5 rounded-ds-full bg-danger-fg ring-2 ring-card"
              aria-hidden
            />
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[min(20rem,calc(100vw-2rem))]" align="end">
        <DropdownMenuLabel>{pt.nav.notificationsBell}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {alerts.length === 0 ? (
          <p className="px-2.5 py-3 text-sm text-muted-foreground">
            {pt.nav.notificationsEmpty}
          </p>
        ) : (
          alerts.map((alert) => {
            const { title, message } = formatAlertCopy(alert);
            return (
              <DropdownMenuItem
                key={alert.id}
                className="flex-col items-stretch gap-0.5"
                onSelect={() => {
                  if (alert.href) navigate(alert.href);
                }}
              >
                <span className="font-medium leading-snug text-foreground">{title}</span>
                {message ? (
                  <span className="text-xs leading-snug text-muted-foreground line-clamp-2">
                    {message}
                  </span>
                ) : null}
              </DropdownMenuItem>
            );
          })
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to={paths.settingsNotifications}
            className="justify-center text-center text-sage-on-tint"
          >
            {pt.nav.notificationsSettingsLink}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NotificationsBell;
